<script lang="ts">
import { onMount, createEventDispatcher } from 'svelte';
import { Issue } from '../models/state';
import { appStore } from '../stores/appStore';
import { AppApiClient } from '../api_contracts/app.client';
import CreateIssue from './IssueCreate.svelte';
import IssueDetails from './IssueDetails.svelte';

	const dispatch = createEventDispatcher();

	// Props
	let { repositoryName, ownerId }: { repositoryName: string; ownerId: string } = $props();

	// State
	let issues: Issue[] = $state([]);
	let selectedFilter = $state('open');
	let searchQuery = $state('');
	let viewMode = $state('list'); // 'list' | 'create' | 'details'
	let selectedIssue: Issue | null = $state(null);
	let loading = $state(true);
	let error = $state('');

	// Counts
	let openCount = $state(0);
	let closedCount = $state(0);
	let totalCount = $state(0);

	// Store
	let client: AppApiClient | null = $state(null);
	let token: string = $state('');

	appStore.subscribe((state) => {
		client = state.client;
		token = state.token;
	});

	// Fetch issues from backend
	async function fetchIssues() {
		if (!client || !token) {
			error = 'Not authenticated';
			loading = false;
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.getIssues({
				token,
				ownerId,
				repositoryName,
				state: selectedFilter,
				page: 1,
				pageSize: 100
			});

			if (result.response.successFull) {
				// Convert backend data to frontend format
				issues = result.response.issues.map((issue) => ({
					id: issue.id,
					repositoryId: issue.repositoryId,
					title: issue.title,
					number: issue.number,
					description: issue.description,
					author: issue.authorName,
					authorAvatar: issue.authorAvatar,
					createdAt: formatDate(issue.createdAt),
					labels: issue.labels.map((label) => ({
						id: label.id,
						name: label.name,
						color: label.color,
						description: label.description
					})),
					comments: issue.commentCount,
					state: issue.state as 'open' | 'closed'
				}));

				openCount = result.response.openCount;
				closedCount = result.response.closedCount;
				totalCount = result.response.totalCount;

				// Dispatch event to update parent component with new counts
				dispatch('countsUpdated', {
					openCount,
					closedCount,
					totalCount
				});
			} else {
				error = result.response.reason || 'Failed to fetch issues';
			}
		} catch (e: any) {
			error = e.message || 'Error fetching issues';
			console.error('Error fetching issues:', e);
		} finally {
			loading = false;
		}
	}

	// Format timestamp to relative time
	function formatDate(timestamp: any): string {
		if (!timestamp) return 'unknown';

		// Handle protobuf Timestamp format
		let date: Date;
		if (timestamp.seconds !== undefined) {
			// Protobuf Timestamp with seconds field (BigInt or Number)
			// Convert BigInt to Number before multiplication
			const seconds = typeof timestamp.seconds === 'bigint'
				? Number(timestamp.seconds)
				: timestamp.seconds;
			date = new Date(seconds * 1000);
		} else if (typeof timestamp === 'string') {
			// ISO string format
			date = new Date(timestamp);
		} else if (timestamp instanceof Date) {
			date = timestamp;
		} else {
			return 'unknown';
		}

		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 1) return 'just now';
		if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
		if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
		if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
		return date.toLocaleDateString();
	}

	// Watch for filter changes
	$effect(() => {
		if (selectedFilter) {
			fetchIssues();
		}
	});

	async function handleCreate(event: CustomEvent) {
		console.log('New issue created:', event.detail);
		viewMode = 'list';
		// Refresh the issue list
		await fetchIssues();
	}

	function showIssueDetails(issue: Issue) {
		selectedIssue = issue;
		viewMode = 'details';
	}

	function closeIssueDetails() {
		selectedIssue = null;
		viewMode = 'list';
		// Refresh the issue list in case the issue was updated
		fetchIssues();
	}

	// Initial load
	onMount(() => {
		fetchIssues();
	});
</script>

{#if viewMode === 'create'}
	<CreateIssue
		{repositoryName}
		{ownerId}
		on:cancel={() => viewMode = 'list'}
		on:create={handleCreate}
	/>
{:else if viewMode === 'details' && selectedIssue}
	<IssueDetails
		issue={selectedIssue}
		{repositoryName}
		{ownerId}
		onClose={closeIssueDetails}
	/>
{:else}
<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
	{#if loading}
		<div class="flex items-center justify-center p-12">
			<div class="text-center">
				<div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
				<p class="mt-4 text-gray-600 dark:text-gray-400">Loading issues...</p>
			</div>
		</div>
	{:else if error}
		<div class="p-12 text-center">
			<svg class="w-16 h-16 mx-auto text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
				<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
			</svg>
			<p class="text-red-600 dark:text-red-400 text-lg font-semibold">{error}</p>
		</div>
	{:else}
	<!-- Header -->
	<div class="p-4 border-b border-gray-200 dark:border-gray-700">
		<div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
			<div class="flex items-center gap-4">
				<button
					onclick={() => (selectedFilter = 'open')}
					class="flex items-center gap-2 text-sm rounded-full px-2 py-1 {selectedFilter === 'open'
						? 'text-gray-900 dark:text-gray-100 font-semibold'
						: 'text-gray-600 dark:text-gray-400'}"
				>
					<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
							clip-rule="evenodd"
						/>
					</svg>
					{openCount} Open
				</button>
				<button
					onclick={() => (selectedFilter = 'closed')}
					class="flex items-center gap-2 text-sm rounded-full px-2 py-1 {selectedFilter === 'closed'
						? 'text-gray-900 dark:text-gray-100 font-semibold'
						: 'text-gray-600 dark:text-gray-400'}"
				>
					<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
							clip-rule="evenodd"
						/>
					</svg>
					{closedCount} Closed
				</button>
			</div>
			<button
				onclick={() => viewMode = 'create'}
				class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
			>
				New Issue
			</button>
		</div>

		<!-- Search and Filters -->
		<div class="mt-4 flex flex-col sm:flex-row gap-2">
			<input
				type="text"
				bind:value={searchQuery}
				placeholder="Search issues..."
				class="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
			/>
			<select
				class="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
			>
				<option>Sort: Newest</option>
				<option>Sort: Oldest</option>
				<option>Sort: Most commented</option>
			</select>
		</div>
	</div>

	<!-- Issues List -->
	<div class="divide-y divide-gray-200 dark:divide-gray-700">
		{#each issues.filter((i) => i.state === selectedFilter) as issue}
			<div class="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
				<div class="flex items-start gap-3">
					<div class="mt-1">
						{#if issue.state === 'open'}
							<svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
									clip-rule="evenodd"
								/>
							</svg>
						{:else}
							<svg class="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
									clip-rule="evenodd"
								/>
							</svg>
						{/if}
					</div>
					<div class="flex-1 min-w-0">
						<div class="flex items-start gap-2">
							<h3
								onclick={() => showIssueDetails(issue)}
								class="text-gray-900 dark:text-gray-100 font-medium hover:text-blue-600 cursor-pointer"
							>
								{issue.title}
							</h3>
							{#each issue.labels as label}
								<span
									class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
									style="background-color: {label.color}20; color: {label.color}; border: 1px solid {label.color}40;"
								>
									{label.name}
								</span>
							{/each}
						</div>
						<div class="mt-1 text-sm text-gray-600 dark:text-gray-400">
							#{issue.number} opened {issue.createdAt} by {issue.author}
						</div>
					</div>
					{#if issue.comments > 0}
						<div class="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-sm">
							<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
									clip-rule="evenodd"
								/>
							</svg>
							{issue.comments}
						</div>
					{/if}
				</div>
			</div>
		{/each}
	</div>

	<!-- Empty State -->
	{#if issues.filter((i) => i.state === selectedFilter).length === 0}
		<div class="p-12 text-center">
			<svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="currentColor" viewBox="0 0 20 20">
				<path
					fill-rule="evenodd"
					d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
					clip-rule="evenodd"
				/>
			</svg>
			<h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No {selectedFilter} issues</h3>
			<p class="text-gray-600 dark:text-gray-400">There are no {selectedFilter} issues at this time.</p>
		</div>
	{/if}
	{/if}
</div>
{/if}
